/**
 * AskUserRenderer — renders an `ask_user` artifact (clarifying questions
 * the agent asks before continuing).
 *
 * Phase 2 of the workspace-focus refactor. The legacy `<AskUserModal>` (a
 * paged wizard popup) is preserved for non-workspace mode; this renderer
 * surfaces the same questions inline in the workspace center column with
 * all questions visible at once and a single Submit at the bottom.
 *
 * Field types match the `ask_user` tool contract:
 *   - "text"          → textarea
 *   - "single_choice" → radio buttons + "Other" inline freetext
 *   - "multi_choice"  → checkbox list + "Other" inline freetext
 *
 * The field-rendering subcomponents are intentionally NOT extracted from
 * `AskUserModal.tsx` — that modal couples its rendering tightly to its
 * paged wizard state (`currentIndex`, `otherText`, single-question flow).
 * Lifting those out would require a refactor that risks breaking the legacy
 * fallback. Duplicate carefully here; commit body notes the duplication so
 * the next consolidation pass knows to look here.
 *
 * Submit dispatches:
 *   onAction("ask_user.respond", artifact, { answers: Record<id, str|str[]> })
 * Skip dispatches:
 *   onAction("ask_user.skip", artifact)
 *
 * Active vs read-only gating: when `sessionType` is set, the renderer
 * subscribes to the namespaced AI Explorer store and only shows the form
 * controls when `askUserProposed` is true for the current session. After
 * the user submits/skips, the store flag flips false and the renderer
 * falls back to a read-only summary of the questions (so the artifact
 * remains visible in history without re-prompting).
 *
 * Spec: .workspace-spec/WORKSPACE_CONTRACT.md
 *       .workspace-spec/PLAN_WORKSPACE_FOCUS.md (Phase 2)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { Artifact, AskUserPayload, AskUserQuestion } from "@/api/models"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { logger } from "@/lib/logger"
import { cn } from "@/lib/utils"
import { getDeepStateForSession, useAIExplorerStore } from "@/stores/ai-explorer-store"

import { ArtifactFrame } from "./ArtifactFrame"
import { isActiveAskUserPrompt } from "./ask-user-correlation"

/** Optional 3rd arg on an ask_user action — submit carries answers, skip
 *  carries nothing. Extra is shaped to match the page-level dispatcher's
 *  union with `{ feedback }` from plan.reject. */
export interface AskUserActionExtra {
  answers?: Record<string, string | string[]>
}

export interface AskUserRendererProps {
  artifact: Artifact & { payload: AskUserPayload }
  versionChain?: Artifact[]
  /**
   * Receives `(actionId, artifact)` for normal artifact actions. The 3rd
   * arg carries `{ answers }` for `ask_user.respond`. Existing dispatchers
   * that only declare `(id, artifact)` still type-check — TS allows
   * ignoring trailing optional params.
   */
  onAction?: (actionId: string, artifact: Artifact, extra?: AskUserActionExtra) => void
  onSelectVersion?: (artifactId: string) => void
  onViewTrace?: (missionId: string) => void
  onShowHistory?: () => void
  /**
   * When set, the renderer subscribes to the matching AI Explorer
   * namespaced store and surfaces the form controls when
   * `askUserProposed` is true. Omit in legacy fallback / unit-test
   * rendering — the form stays hidden and only the read-only summary
   * renders.
   */
  sessionType?: string
}

function answerList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : []
}

interface QuestionFieldProps {
  question: AskUserQuestion
  value: string | string[]
  otherValue: string
  onChange: (value: string | string[]) => void
  onOtherChange: (value: string) => void
}

/** Single-question field — handles text / single_choice / multi_choice. */
function QuestionField({
  question,
  value,
  otherValue,
  onChange,
  onOtherChange,
}: QuestionFieldProps) {
  const { t } = useTranslation()
  const hasOptions = (question.options?.length ?? 0) > 0

  // Pure text question (no options): render a textarea.
  if (question.type === "text" && !hasOptions) {
    return (
      <Textarea
        className="min-h-[80px] resize-y text-sm"
        data-testid={`ask-user-text-${question.id}`}
        placeholder={t("deepExplorer.ask_user.your_answer")}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
      />
    )
  }

  // Choice question (single or multi): render option buttons + "Other".
  return (
    <div className="flex flex-col gap-1.5">
      {(question.options ?? []).map((option) => {
        const isSelected =
          question.type === "multi_choice" ? answerList(value).includes(option) : value === option

        return (
          <button
            key={option}
            data-testid={`ask-user-option-${question.id}-${option}`}
            type="button"
            className={cn(
              "flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors",
              "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
              isSelected
                ? "border-primary bg-primary/5 text-primary font-medium"
                : "border-border bg-background text-foreground hover:bg-muted/50"
            )}
            onClick={() => {
              if (question.type === "multi_choice") {
                const current = answerList(value)
                const next = current.includes(option)
                  ? current.filter((v) => v !== option)
                  : [...current, option]
                onChange(next)
              } else {
                onChange(option)
              }
            }}
          >
            {question.type === "multi_choice" && (
              <span
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded border text-xs",
                  isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border"
                )}
              >
                {isSelected && "✓"}
              </span>
            )}
            {question.type === "single_choice" && (
              <span
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-full border",
                  isSelected ? "border-primary" : "border-border"
                )}
              >
                {isSelected && <span className="bg-primary size-2 rounded-full" />}
              </span>
            )}
            <span>{option}</span>
          </button>
        )
      })}

      {/* Inline "Other" freetext — alternative to a suggested option.
          For single_choice it directly sets the answer (deselects suggestions);
          for multi_choice it adds on Enter / blur and clears the box. */}
      <div
        className={cn(
          "flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-[box-shadow,border-color,background-color] duration-200 ease-out",
          "focus-within:border-primary focus-within:shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]",
          otherValue ? "border-primary bg-primary/5" : "border-border border-dashed"
        )}
      >
        <span className="text-muted-foreground shrink-0 text-xs">
          {t("deepExplorer.ask_user.other_label")}
        </span>
        <Input
          // The wrapper above owns the focus glow (focus-within), so the input
          // must stay chromeless — `focus-visible:ring-0`, matching how the
          // glow is expressed on ui/Input.
          className="h-auto flex-1 border-0 bg-transparent p-0 text-sm shadow-none focus-visible:border-transparent focus-visible:ring-0"
          data-testid={`ask-user-other-${question.id}`}
          placeholder={t("deepExplorer.ask_user.type_own_answer")}
          type="text"
          value={otherValue}
          onBlur={() => {
            if (question.type === "multi_choice" && otherValue.trim()) {
              const current = answerList(value)
              const trimmed = otherValue.trim()
              if (!current.includes(trimmed)) {
                onChange([...current, trimmed])
              }
              onOtherChange("")
            }
          }}
          onChange={(e) => {
            const val = e.target.value
            onOtherChange(val)
            if (question.type !== "multi_choice") {
              onChange(val)
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && question.type === "multi_choice") {
              e.preventDefault()
              const trimmed = otherValue.trim()
              if (trimmed) {
                const current = answerList(value)
                if (!current.includes(trimmed)) {
                  onChange([...current, trimmed])
                }
                onOtherChange("")
              }
            }
          }}
        />
      </div>
    </div>
  )
}

interface AskUserFormProps {
  artifact: Artifact & { payload: AskUserPayload }
  onAction?: (actionId: string, artifact: Artifact, extra?: AskUserActionExtra) => void
}

/** Active-state form: all questions visible at once, single Submit + Skip. */
function AskUserForm({ artifact, onAction }: AskUserFormProps) {
  const { t } = useTranslation()
  const questions = artifact.payload.questions

  // Seed initial answers from question defaults / type. Multi-choice starts
  // as an empty array (so toggling adds items); other types as empty string
  // (or the question's `default` if provided).
  const [answers, setAnswers] = useState<Record<string, string | string[]>>(() => {
    const initial: Record<string, string | string[]> = {}
    for (const q of questions) {
      if (q.type === "multi_choice") {
        initial[q.id] = []
      } else {
        initial[q.id] = q.default ?? ""
      }
    }
    return initial
  })
  const [otherTexts, setOtherTexts] = useState<Record<string, string>>({})

  const handleChange = useCallback((id: string, value: string | string[]) => {
    setAnswers((prev) => ({ ...prev, [id]: value }))
  }, [])

  const handleOtherChange = useCallback((id: string, value: string) => {
    setOtherTexts((prev) => ({ ...prev, [id]: value }))
  }, [])

  const allAnswered = useMemo(() => {
    return questions.every((q) => {
      const a = answers[q.id]
      if (q.type === "multi_choice") {
        // Accept either a checked option or a non-empty "Other" freetext.
        // Without the otherTexts check, a user who types into "Other"
        // without pressing Enter/blur sees Submit stay disabled even
        // though their answer is captured at submit time (the handler
        // folds otherTexts into the dispatched answers).
        return answerList(a).length > 0 || (otherTexts[q.id] ?? "").trim().length > 0
      }
      // Pure-text questions are optional — the agent uses them for
      // supplementary detail (e.g. "If you picked Other, specify which").
      // Required answers belong in single_choice / multi_choice. The
      // tool result on submit becomes ``A:`` (empty) for skipped text
      // fields, which the model treats the same as "(no answer)".
      if (q.type === "text") {
        return true
      }
      // single_choice — string truthy check. The "Other" freetext is folded
      // into `answers` directly for single_choice, so this covers both.
      return typeof a === "string" && a.trim().length > 0
    })
  }, [answers, questions, otherTexts])

  const handleSubmit = useCallback(() => {
    // Fold any unsubmitted multi_choice "Other" text into answers before
    // dispatching, so the user doesn't have to press Enter / blur first.
    const finalAnswers: Record<string, string | string[]> = { ...answers }
    for (const q of questions) {
      if (q.type === "multi_choice") {
        const otherText = (otherTexts[q.id] ?? "").trim()
        if (otherText) {
          const current = answerList(finalAnswers[q.id])
          if (!current.includes(otherText)) {
            finalAnswers[q.id] = [...current, otherText]
          }
        }
      }
    }
    onAction?.("ask_user.respond", artifact, { answers: finalAnswers })
  }, [answers, artifact, onAction, otherTexts, questions])

  const handleSkip = useCallback(() => {
    onAction?.("ask_user.skip", artifact)
  }, [artifact, onAction])

  return (
    <div className="flex flex-col gap-4" data-testid="ask-user-form">
      {questions.map((q, i) => (
        <div key={q.id} className="flex flex-col gap-2" data-testid={`ask-user-question-${q.id}`}>
          <p className="text-foreground text-sm leading-snug font-medium">
            <span className="text-muted-foreground mr-1.5 text-xs font-normal">{i + 1}.</span>
            {q.question}
          </p>
          <QuestionField
            otherValue={otherTexts[q.id] ?? ""}
            question={q}
            value={answers[q.id] ?? ""}
            onChange={(v) => handleChange(q.id, v)}
            onOtherChange={(v) => handleOtherChange(q.id, v)}
          />
        </div>
      ))}

      <div className="border-border/60 flex items-center justify-end gap-2 border-t pt-3">
        <Button data-testid="ask-user-skip" size="sm" variant="ghost" onClick={handleSkip}>
          {t("deepExplorer.ask_user.skip_all", { defaultValue: "Skip" })}
        </Button>
        <Button
          data-testid="ask-user-submit"
          disabled={!allAnswered}
          size="sm"
          variant="default"
          onClick={handleSubmit}
        >
          {t("deepExplorer.ask_user.submit", { defaultValue: "Submit" })}
        </Button>
      </div>
    </div>
  )
}

interface AskUserSummaryProps {
  payload: AskUserPayload
}

function formatAnswer(answer: string | string[] | undefined): string | null {
  if (Array.isArray(answer)) {
    const trimmed = answer.map((a) => a.trim()).filter(Boolean)
    return trimmed.length > 0 ? trimmed.join(", ") : null
  }
  if (typeof answer === "string") {
    const trimmed = answer.trim()
    return trimmed.length > 0 ? trimmed : null
  }
  return null
}

/** Read-only summary — used after submit/skip, or in legacy fallback. When the
 *  backend has patched the artifact with submitted ``answers`` (post-submit),
 *  each question renders with its answer in a muted block underneath. Skipped
 *  / empty answers render a "(no answer)" placeholder so the user can tell
 *  the difference between "they answered" and "they skipped". */
function AskUserSummary({ payload }: AskUserSummaryProps) {
  const { t } = useTranslation()
  const questions = payload.questions
  const answers = payload.answers ?? {}
  const hasAnswers = Object.keys(answers).length > 0

  return (
    <div className="flex flex-col gap-2" data-testid="ask-user-summary">
      {questions.length === 0 ? (
        <p className="text-muted-foreground py-2 text-xs italic">
          The agent asked for clarification but provided no questions.
        </p>
      ) : (
        <ul className="space-y-2">
          {questions.map((q, i) => {
            const formattedAnswer = formatAnswer(answers[q.id])
            return (
              <li
                key={q.id}
                className="flex flex-col gap-1 text-sm"
                data-testid={`ask-user-summary-item-${q.id}`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground mt-0.5 shrink-0 text-xs">{i + 1}.</span>
                  <span className="text-foreground/90 flex-1">{q.question}</span>
                </div>
                {hasAnswers ? (
                  <div
                    className="bg-muted/30 text-foreground ml-5 rounded-md px-2.5 py-1.5 text-xs"
                    data-testid={`ask-user-summary-answer-${q.id}`}
                  >
                    {formattedAnswer ?? (
                      <span className="text-muted-foreground italic">
                        {t("deepExplorer.ask_user.no_answer", {
                          defaultValue: "(no answer)",
                        })}
                      </span>
                    )}
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

interface AskUserGateProps {
  artifact: Artifact & { payload: AskUserPayload }
  sessionType: string
  onAction?: (actionId: string, artifact: Artifact, extra?: AskUserActionExtra) => void
}

/** Subscribes to the AI Explorer store and renders the form when
 *  this artifact exactly matches the pending ask; otherwise renders the
 *  read-only summary. */
function AskUserGate({ artifact, sessionType, onAction }: AskUserGateProps) {
  const explorerStore = useAIExplorerStore(sessionType)
  const askUserProposed = explorerStore(
    (state) => getDeepStateForSession(state, state.currentSessionId).askUserProposed
  )
  const pendingAskId = explorerStore(
    (state) => getDeepStateForSession(state, state.currentSessionId).askUserPayload?.ask_id
  )
  const artifactAskId = artifact.payload.ask_id
  const isActiveAsk = isActiveAskUserPrompt({
    askUserProposed,
    pendingAskId,
    artifactAskId,
  })

  // Legacy parity: `AskUserModal` auto-sent an empty response whenever the
  // backend emitted ask_user with no questions (defensive guard against a
  // malformed tool payload that would otherwise pause the run forever).
  // Mirror that here so the new renderer doesn't silently regress. The ref
  // pins the auto-skip to a single artifact id, so a re-render with the
  // same empty payload doesn't double-fire and a future ask_user artifact
  // with empty questions still gets its own skip.
  const autoSkippedIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (
      isActiveAsk &&
      artifact.payload.questions.length === 0 &&
      autoSkippedIdRef.current !== artifact.id
    ) {
      autoSkippedIdRef.current = artifact.id
      onAction?.("ask_user.skip", artifact)
    }
  }, [artifact, isActiveAsk, onAction])

  if (isActiveAsk) {
    return <AskUserForm artifact={artifact} onAction={onAction} />
  }
  return <AskUserSummary payload={artifact.payload} />
}

export function AskUserRenderer({
  artifact,
  versionChain,
  onAction,
  onSelectVersion,
  onViewTrace,
  onShowHistory,
  sessionType,
}: AskUserRendererProps) {
  const payload = artifact.payload
  const context = payload.context.trim()

  // Defensive — if the artifact has no questions, log and still render an
  // explanatory shell rather than blanking out. Submit is gated by
  // `allAnswered` so the user can't dispatch an empty-answers payload.
  if (payload.questions.length === 0) {
    logger.warn(
      `AskUserRenderer: artifact ${artifact.id} has no questions — rendering empty-state summary`
    )
  }

  return (
    <ArtifactFrame
      artifact={artifact}
      className="ask-user-card"
      versionChain={versionChain}
      onAction={onAction}
      onSelectVersion={onSelectVersion}
      onShowHistory={onShowHistory}
      onViewTrace={onViewTrace}
    >
      <div className="space-y-4 text-sm" data-testid="ask-user-renderer-body">
        {context ? (
          <p className="text-muted-foreground leading-relaxed" data-testid="ask-user-context">
            {context}
          </p>
        ) : null}

        {sessionType ? (
          <AskUserGate artifact={artifact} sessionType={sessionType} onAction={onAction} />
        ) : (
          // Legacy / test rendering without sessionType: read-only summary.
          <AskUserSummary payload={payload} />
        )}
      </div>
    </ArtifactFrame>
  )
}
